// Zod-схемы namespace `profile` — личные данные, аватар, онбординг.
// Источник: docs/DOMAIN-MODEL.md §3 + макеты «IEC ID Auth.dc.html» (4b, пять
// шагов) и «IEC ID Sections.dc.html» (3d · Personal data).
import { z } from "zod";
import { ECOSYSTEM_LOCALES, FALLBACK_LOCALE } from "../../constants/locales.js";
import {
	EmailSchema,
	IsoDateSchema,
	IsoDateTimeSchema,
	LocaleSchema,
	ServiceIdSchema,
	UuidV7Schema,
} from "../_shared/primitives.js";

/**
 * Локаль ПРОФИЛЯ — не любые два строчных латинских символа, а код из
 * `ECOSYSTEM_LOCALES` (Д11).
 *
 * `LocaleSchema` (регэксп `^[a-z]{2}$`) остаётся правильным типом хранения:
 * колонка `TEXT` с ISO 639-1, набор растёт без миграции. Но на ВХОДЕ он
 * пропускал `xx`, и это значение уезжало в SET-события и в userinfo сервисов,
 * где ему взяться неоткуда (В6). Профиль — единственное место, где локаль
 * назначается человеком, поэтому сверка с реестром живёт здесь.
 */
export const ProfileLocaleSchema = z.enum(ECOSYSTEM_LOCALES);

/**
 * Локаль профиля с фолбэком. Пустая или неизвестная строка (импорт, старый
 * SSO, сервис экосистемы) превращается в английский, а НЕ в русский: русский
 * не должен показываться нерусскому пользователю нигде (Д11).
 */
export function toProfileLocale(value: string | null | undefined): string {
	const parsed = ProfileLocaleSchema.safeParse(value);
	return parsed.success ? parsed.data : FALLBACK_LOCALE;
}

/**
 * Возрастная группа — единственная точка проверки возраста в экосистеме
 * (инвариант №4). Сервисы читают её из профиля и не спрашивают дату рождения.
 */
export const AgeGroupSchema = z.enum(["under16", "16-17", "18+"]);

/** Минимальный возраст экосистемы. Ниже — регистрация не завершается. */
export const MIN_AGE = 16;

export const GenderSchema = z.enum(["female", "male", "undisclosed"]);

/** Ник виден другим участникам в сервисах. Нормализуется в нижний регистр. */
/**
 * Ник виден другим участникам в сервисах. Хранится в нижнем регистре.
 *
 * Порядок операций важен: обрезка и нижний регистр идут ДО проверки формы.
 * Раньше `regex` требовал нижний регистр, а `.toLowerCase()` шёл после него —
 * `Olga` отвергался в `profile.update`, хотя `checkNickname` (нормализующий
 * сам) только что сказал «свободен» (аудит, «Можно после»).
 */
export const NicknameSchema = z
	.string()
	.trim()
	.toLowerCase()
	.pipe(
		z
			.string()
			.min(3)
			.max(32)
			.regex(/^[a-z0-9][a-z0-9._-]*$/, "Ник — латиница, цифры, точка, дефис, подчёркивание"),
	);

/** IANA-зона (`Europe/Prague`). Список валидируется на сервере по `Intl`. */
export const TimezoneSchema = z.string().min(3).max(64);

/** ISO 3166-1 alpha-2 в верхнем регистре. */
export const CountryCodeSchema = z.string().regex(/^[A-Z]{2}$/, "Страна — код ISO 3166-1 alpha-2");

/** Ключ объекта аватара в S3, выданный `uploadAvatarUrl`. */
export const AvatarKeySchema = z.string().min(8).max(256);

/** Настройки из userinfo старого SSO — формат обязан сохраниться (§10.6). */
export const ProfileSettingsSchema = z.object({
	autoTranslate: z.boolean(),
	showMyParticipationPrograms: z.boolean(),
	colorScheme: z.string().min(2).max(32),
	serifFont: z.boolean(),
});

export const ProfileSchema = z.object({
	userId: UuidV7Schema,
	/** Почта — идентификатор, меняется только через поддержку (инвариант №2). */
	email: EmailSchema,
	emailVerified: z.boolean(),
	/**
	 * До первого шага мастера имени ещё нет. Раньше контракт объявлял его
	 * обязательным, маппер подставлял пустую строку, и из-за расхождения
	 * `.output()` пришлось снять с трёх процедур. `nullable` честнее: пустая
	 * строка — это не «нет имени», а «имя длиной ноль».
	 */
	firstName: z.string().max(100).nullable(),
	lastName: z.string().max(100).nullable(),
	nickname: NicknameSchema.nullable(),
	/**
	 * Всегда абсолютный URL `GET /api/avatar/{userId}`: своё фото или
	 * DiceBear-заглушка. Есть ли загруженное фото — `hasCustomAvatar`.
	 */
	avatarUrl: z.url().nullable(),
	/** `true` — человек загрузил или импортировал фото, не заглушку. */
	hasCustomAvatar: z.boolean(),
	birthDate: IsoDateSchema.nullable(),
	/**
	 * `null` = возраст ЕЩЁ НЕ ПОДТВЕРЖДЁН. Раньше здесь отдавался `under16`,
	 * и сервис, читающий профиль, закрывал взрослому человеку доступ только
	 * потому, что тот не дошёл до шага «About». «Не знаем» и «младше 16» —
	 * разные ответы, и различать их обязан контракт, а не комментарий.
	 */
	ageGroup: AgeGroupSchema.nullable(),
	ageConfirmedAt: IsoDateTimeSchema.nullable(),
	gender: GenderSchema.nullable(),
	locale: LocaleSchema,
	country: CountryCodeSchema.nullable(),
	timezone: TimezoneSchema,
	/** Рейтинг участника. В userinfo старого SSO уходит строкой — здесь число. */
	rating: z.number(),
	settings: ProfileSettingsSchema,
	onboardingCompletedAt: IsoDateTimeSchema.nullable(),
	/** Оптимистическая блокировка: `update` отклоняется при рассинхроне. */
	version: z.number().int().positive(),
	createdAt: IsoDateTimeSchema,
	updatedAt: IsoDateTimeSchema,
});

// ─── чтение и правка ────────────────────────────────────────────────────────

export const ProfileGetOutputSchema = ProfileSchema;

export const ProfileUpdateInputSchema = z.object({
	firstName: z.string().min(1).max(100).optional(),
	lastName: z.string().max(100).nullish(),
	nickname: NicknameSchema.optional(),
	birthDate: IsoDateSchema.optional(),
	gender: GenderSchema.nullish(),
	locale: ProfileLocaleSchema.optional(),
	country: CountryCodeSchema.nullish(),
	timezone: TimezoneSchema.optional(),
	/** Ключ загруженного объекта из `uploadAvatarUrl` — им аватар и фиксируется. */
	avatarKey: AvatarKeySchema.optional(),
	settings: ProfileSettingsSchema.partial().optional(),
	/** Версия, прочитанная клиентом. Несовпадение = конфликт правок. */
	expectedVersion: z.number().int().positive().optional(),
});

export const ProfileUpdateOutputSchema = ProfileSchema;

// ─── аватар ─────────────────────────────────────────────────────────────────

export const AvatarContentTypeSchema = z.enum(["image/jpeg", "image/png", "image/webp"]);

export const UploadAvatarUrlInputSchema = z.object({
	contentType: AvatarContentTypeSchema,
	sizeBytes: z.number().int().positive(),
});

export const UploadAvatarUrlOutputSchema = z.object({
	/** Presigned PUT в S3. Загрузка идёт мимо нашего сервера. */
	uploadUrl: z.url(),
	/** Ключ объекта — передаётся обратно в `profile.update`. */
	avatarKey: AvatarKeySchema,
	expiresAt: IsoDateTimeSchema,
	maxSizeBytes: z.number().int().positive(),
});

export const RemoveAvatarOutputSchema = ProfileSchema;

// ─── ник ────────────────────────────────────────────────────────────────────

export const CheckNicknameInputSchema = z.object({
	nickname: z.string().min(1).max(32),
});

export const CheckNicknameOutputSchema = z.object({
	available: z.boolean(),
	/** Ник в том виде, в котором он будет сохранён. */
	normalized: z.string(),
	reason: z.enum(["taken", "reserved", "too-short", "too-long", "invalid"]).nullable(),
	suggestions: z.array(z.string()),
});

/**
 * Автоподбор ника. Основная база — локальная часть почты, её сервер берёт сам.
 * `from` — имя с первого шага: запасная база на случай почты, из которой
 * латиницы не собрать (адрес на кириллице, IDN).
 */
export const SuggestNicknameInputSchema = z.object({
	from: z.string().max(100).optional(),
});

export const SuggestNicknameOutputSchema = z.object({
	/** Первый свободный вариант — его и подставляет мастер. `null` — не нашлось. */
	nickname: z.string().nullable(),
	/** Ещё несколько свободных вариантов на выбор. */
	alternatives: z.array(z.string()),
});

// ─── онбординг ──────────────────────────────────────────────────────────────

/** Пять экранов мастера (макет 4b). `done` — терминальный шаг с бонусом. */
export const OnboardingStepSchema = z.enum(["identity", "about", "locale", "documents", "done"]);

/** Документ, который мастер обязан собрать на шаге «Documents». */
export const OnboardingDocumentSchema = z.object({
	key: z.string().min(2).max(64),
	title: z.string(),
	version: z.string().min(1).max(16),
	sha256: z.string().length(64),
	requiresAcceptance: z.boolean(),
	url: z.string().min(1),
});

/** Черновик мастера: то, что уже введено, чтобы шаг можно было переоткрыть. */
export const OnboardingDraftSchema = z.object({
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	nickname: z.string().nullable(),
	/**
	 * Канонический адрес картинки. Пустым НЕ бывает: обработчик
	 * `/api/avatar/{userId}` отдаёт фото либо рисует заглушку сам — состояния
	 * «аватара нет» в экосистеме не существует.
	 */
	avatarUrl: z.url().nullable(),
	/**
	 * Загружено ли СВОЁ фото. Отдельно от `avatarUrl` именно потому, что тот
	 * непустой всегда: без этого признака кнопка на шаге подписана «Заменить
	 * фото» человеку, который ничего не загружал.
	 */
	hasCustomAvatar: z.boolean(),
	birthDate: IsoDateSchema.nullable(),
	gender: GenderSchema.nullable(),
	country: CountryCodeSchema.nullable(),
	locale: LocaleSchema.nullable(),
	timezone: TimezoneSchema.nullable(),
});

export const OnboardingStateOutputSchema = z.object({
	step: OnboardingStepSchema,
	/** Номер шага для «Step N of M» — от 1. */
	stepIndex: z.number().int().min(1),
	totalSteps: z.number().int().positive(),
	completed: z.boolean(),
	/**
	 * Профиль приехал переносом из Портала (`profiles.imported_at`), а не
	 * заведён у нас с нуля.
	 *
	 * Единственный признак, по которому мастеру разрешено открыться НЕ на первом
	 * экране: пропускать можно лишь то, что человек вводил сам в другой нашей
	 * системе. Всё остальное — черновик, подставленный в поля, и человек
	 * подтверждает его нажатием (решение владельца 25.08.2026).
	 */
	imported: z.boolean(),
	completedSteps: z.array(OnboardingStepSchema),
	draft: OnboardingDraftSchema,
	/** Что осталось принять на шаге «Documents». */
	pendingDocuments: z.array(OnboardingDocumentSchema),
	/** Бонус за завершение (класс B), показывается на пятом экране. */
	rewardPoints: z.number().int().nonnegative(),
	/** Сервис, из которого пришёл человек, — кнопка «Go to …» на финале. */
	returnServiceId: ServiceIdSchema.nullable(),
});

/** Шаг мастера — discriminated union по `step`: у каждого экрана свои поля. */
export const SubmitOnboardingStepInputSchema = z.discriminatedUnion("step", [
	z.object({
		step: z.literal("identity"),
		firstName: z.string().min(1).max(100),
		lastName: z.string().max(100).nullish(),
		nickname: NicknameSchema,
		// Фото сюда больше не приходит — оно спрашивается на шаге «about», где
		// уже известен пол (от него зависит заглушка). Поле оставлено
		// принимаемым: старая вкладка мастера, открытая до выката, дошлёт его
		// сюда, и терять загруженную картинку из-за этого незачем.
		avatarKey: AvatarKeySchema.nullish(),
	}),
	z.object({
		step: z.literal("about"),
		birthDate: IsoDateSchema,
		gender: GenderSchema,
		/** Фото: экран пола и есть экран аватара (решение владельца 2026-08-22). */
		avatarKey: AvatarKeySchema.nullish(),
		// Страны здесь нет: мастер её больше не спрашивает (решение владельца
		// 2026-08-21). Колонка `profiles.country` осталась — её пишет админка и
		// перенос из старого SSO, — но платёжный контур выбирается по языку
		// профиля, а не по ней.
	}),
	z.object({
		step: z.literal("locale"),
		locale: ProfileLocaleSchema,
		timezone: TimezoneSchema,
	}),
	z.object({
		step: z.literal("documents"),
		/** Обязательные документы Legal Center: ключ + версия + хеш текста. */
		accepted: z
			.array(
				z.object({
					key: z.string().min(2).max(64),
					version: z.string().min(1).max(16),
					sha256: z.string().length(64),
				}),
			)
			.min(1),
		/**
		 * Необязательные согласия. Мастер их здесь БОЛЬШЕ НЕ ШЛЁТ — все три
		 * переехали на экран «Уведомления и рассылки», от которого зависят
		 * приветственные Баллы (решение владельца 23.08.2026).
		 *
		 * Поле оставлено принимаемым: вкладка мастера, открытая до выката,
		 * дошлёт его сюда, и терять из-за этого решение человека незачем.
		 */
		optional: z
			.array(z.object({ key: z.string().min(2).max(64), enabled: z.boolean() }))
			.optional(),
	}),
]);

/**
 * Результат шага. `rejected` — единственный способ вернуть «моложе 16»:
 * регистрация не завершается, вместо неё предлагается экспорт и удаление.
 */
export const SubmitOnboardingStepOutputSchema = z.discriminatedUnion("state", [
	z.object({
		state: z.literal("next"),
		step: OnboardingStepSchema,
		stepIndex: z.number().int().min(1),
		totalSteps: z.number().int().positive(),
	}),
	z.object({
		state: z.literal("ready"),
		/** Все шаги пройдены — остаётся `completeOnboarding`. */
		rewardPoints: z.number().int().nonnegative(),
	}),
	z.object({
		state: z.literal("rejected"),
		reason: z.literal("under-min-age"),
		minAge: z.number().int().positive(),
		ageGroup: AgeGroupSchema,
	}),
]);

/**
 * Задача «включить уведомления и рассылки» — она даёт приветственные Баллы
 * (решение владельца 23.08.2026).
 *
 * Части условия отдаются по отдельности намеренно: финальный экран мастера и
 * список задач витрины показывают РАЗНОЕ. Мастеру нужно «включил ли человек
 * прямо сейчас», витрине — «получил ли он уже». Один булев флаг на оба вопроса
 * однажды соврал бы одному из них.
 */
export const WelcomeRewardOutputSchema = z.object({
	/** Сколько Баллов даёт задача. */
	points: z.number().int().nonnegative(),
	/** Есть живая подписка на push хотя бы на одном устройстве. */
	pushEnabled: z.boolean(),
	/** Приняты ВСЕ необязательные согласия. */
	consentsAccepted: z.boolean(),
	/** Баллы уже начислены — задачу показывать больше не нужно. */
	alreadyRewarded: z.boolean(),
	/** Мастер регистрации пройден: без него задача не считается вовсе. */
	onboardingCompleted: z.boolean(),
});

export const CompleteOnboardingInputSchema = z.object({
	/** Куда вернуть человека: сервис-инициатор или витрина IEC ID. */
	serviceId: ServiceIdSchema.optional(),
});

export const CompleteOnboardingOutputSchema = z.object({
	completed: z.literal(true),
	completedAt: IsoDateTimeSchema,
	/**
	 * Сколько Баллов начислено ПРЯМО СЕЙЧАС. Ноль — либо повторный вызов
	 * (идемпотентность), либо задача «включить уведомления и рассылки» ещё не
	 * выполнена: с 23.08.2026 приветственные Баллы даёт она, а не сам факт
	 * финала мастера (решение владельца).
	 */
	awardedPoints: z.number().int().nonnegative(),
	/**
	 * Награда ещё ждёт человека. Финальный экран мастера показывает по этому
	 * полю одно из двух состояний — «+300 начислены» либо «300 Баллов ждут
	 * вас», — и экран, обещающий начисленное без начисления, был бы враньём.
	 */
	rewardPending: z.boolean(),
	redirectTo: z.url().nullable(),
});
